import { Card, Statistic, Row, Col, Progress, Table, Tag, Timeline } from 'antd'
import { 
  CheckCircleOutlined, 
  WarningOutlined, 
  CloseCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  RobotOutlined
} from '@ant-design/icons'

const SystemMonitor = () => {
  const serviceStatus = [
    { name: '后端服务', status: 'normal', latency: '25ms' },
    { name: '数据库(PostgreSQL)', status: 'normal', latency: '15ms' },
    { name: '缓存(Redis)', status: 'normal', latency: '5ms' },
    { name: '搜索引擎(ES)', status: 'normal', latency: '30ms' },
    { name: '图数据库(Neo4j)', status: 'normal', latency: '45ms' },
    { name: '向量数据库(Milvus)', status: 'warning', latency: '120ms' },
    { name: 'OCR服务', status: 'normal', latency: '200ms' },
    { name: '模型推理服务', status: 'normal', latency: '500ms' },
  ]

  const recentAlerts = [
    { time: '15:30:25', level: 'warning', message: 'Milvus响应时间超过100ms' },
    { time: '14:20:10', level: 'info', message: '系统自动备份完成' },
    { time: '12:15:33', level: 'error', message: 'OCR服务短暂不可用，已自动恢复' },
    { time: '10:05:00', level: 'info', message: '模型微调任务 FT-20260222-002 开始训练' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">📊 系统监控</h1>
      </div>

      {/* 系统状态概览 */}
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="服务状态"
              value={7}
              suffix="/ 8 正常"
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃用户数"
              value={12}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="正在处理任务"
              value={5}
              prefix={<RobotOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="队列等待"
              value={3}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 资源使用 */}
      <Row gutter={16}>
        <Col span={8}>
          <Card title="CPU使用率">
            <Progress percent={45} status="active" />
            <div className="mt-2 text-sm text-gray-500">
              8核心 | 平均负载: 3.2
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="内存使用">
            <Progress percent={62} status="active" strokeColor="#1890ff" />
            <div className="mt-2 text-sm text-gray-500">
              已用 49.6GB / 总 80GB
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="磁盘使用">
            <Progress percent={78} status="active" strokeColor="#faad14" />
            <div className="mt-2 text-sm text-gray-500">
              已用 780GB / 总 1TB
            </div>
          </Card>
        </Col>
      </Row>

      {/* GPU状态 */}
      <Card title="🎮 GPU状态">
        <Row gutter={16}>
          {[1, 2, 3, 4].map((i) => (
            <Col span={6} key={i}>
              <Card className="bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">GPU {i}</span>
                  <Tag color="success">正常</Tag>
                </div>
                <div className="text-sm text-gray-500 mb-2">NVIDIA A100 80GB</div>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>利用率</span>
                      <span>{65 + i * 5}%</span>
                    </div>
                    <Progress percent={65 + i * 5} showInfo={false} />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>显存</span>
                      <span>{55 + i}GB / 80GB</span>
                    </div>
                    <Progress percent={(55 + i) / 80 * 100} showInfo={false} />
                  </div>
                  <div className="text-xs text-gray-500">
                    温度: {65 + i}°C
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* 服务状态详情 */}
      <Row gutter={16}>
        <Col span={16}>
          <Card title="服务状态详情">
            <Table
              dataSource={serviceStatus}
              rowKey="name"
              pagination={false}
              columns={[
                { title: '服务名称', dataIndex: 'name', key: 'name' },
                { 
                  title: '状态', 
                  dataIndex: 'status', 
                  key: 'status',
                  render: (status: string) => {
                    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
                      normal: { color: 'success', icon: <CheckCircleOutlined />, text: '正常' },
                      warning: { color: 'warning', icon: <WarningOutlined />, text: '警告' },
                      error: { color: 'error', icon: <CloseCircleOutlined />, text: '异常' },
                    }
                    const cfg = config[status]
                    return <Tag color={cfg.color} icon={cfg.icon}>{cfg.text}</Tag>
                  }
                },
                { 
                  title: '响应时间', 
                  dataIndex: 'latency', 
                  key: 'latency',
                  render: (text: string, record: any) => (
                    <span style={{ color: record.status === 'warning' ? '#faad14' : 'inherit' }}>
                      {text}
                    </span>
                  )
                },
              ]}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="最近告警">
            <Timeline mode="left">
              {recentAlerts.map((alert, idx) => (
                <Timeline.Item
                  key={idx}
                  label={alert.time}
                  color={alert.level === 'error' ? 'red' : alert.level === 'warning' ? 'orange' : 'blue'}
                >
                  {alert.message}
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default SystemMonitor
